<template>
  <div class="dashboard-page">
    <div class="page-header">
      <div>
        <h2>Análise Gráfica em Documentos e Projetos</h2>
        <p class="page-subtitle">
          Painel gerencial para acompanhamento de processos, documentos, projetos, pendências e evolução das análises.
        </p>
      </div>

      <div class="header-actions">
        <button class="btn btn-secondary" @click="carregarMock">Recarregar</button>
        <button class="btn btn-primary">Exportar resumo</button>
      </div>
    </div>

    <!-- filtros -->
    <div class="panel-card">
      <div class="card-title">Filtros</div>

      <div class="filters-grid">
        <div class="field">
          <label>Período inicial</label>
          <input type="date" v-model="filtros.periodoInicial" />
        </div>

        <div class="field">
          <label>Período final</label>
          <input type="date" v-model="filtros.periodoFinal" />
        </div>

        <div class="field">
          <label>Município</label>
          <select v-model="filtros.municipio">
            <option>Todos</option>
            <option>Araranguá</option>
            <option>Sombrio</option>
            <option>Turvo</option>
            <option>Meleiro</option>
          </select>
        </div>

        <div class="field">
          <label>Tipo de processo</label>
          <select v-model="filtros.tipoProcesso">
            <option>Todos</option>
            <option>Registro de Estabelecimento</option>
            <option>Registro de Produto</option>
            <option>Registro de Rótulo</option>
            <option>Projeto Arquitetônico</option>
          </select>
        </div>

        <div class="field">
          <label>Status</label>
          <select v-model="filtros.status">
            <option>Todos</option>
            <option>Em análise</option>
            <option>Aprovado</option>
            <option>Com pendências</option>
            <option>Reprovado</option>
          </select>
        </div>

        <div class="field field-actions">
          <label>&nbsp;</label>
          <button class="btn btn-primary btn-full">Aplicar filtros</button>
        </div>
      </div>
    </div>

    <!-- KPIs -->
    <div class="kpi-grid">
      <div class="kpi-card">
        <div class="kpi-label">Total de processos</div>
        <div class="kpi-value">{{ kpis.totalProcessos }}</div>
      </div>

      <div class="kpi-card">
        <div class="kpi-label">Em análise</div>
        <div class="kpi-value text-info">{{ kpis.emAnalise }}</div>
      </div>

      <div class="kpi-card">
        <div class="kpi-label">Aprovados</div>
        <div class="kpi-value text-success">{{ kpis.aprovados }}</div>
      </div>

      <div class="kpi-card">
        <div class="kpi-label">Com pendências</div>
        <div class="kpi-value text-warning">{{ kpis.comPendencias }}</div>
      </div>

      <div class="kpi-card">
        <div class="kpi-label">Reprovados</div>
        <div class="kpi-value text-danger">{{ kpis.reprovados }}</div>
      </div>

      <div class="kpi-card">
        <div class="kpi-label">Tempo médio</div>
        <div class="kpi-value">{{ kpis.tempoMedioDias }} dias</div>
      </div>
    </div>

    <!-- linha 1 -->
    <div class="charts-grid charts-grid-2">
      <!-- distribuição por status -->
      <div class="panel-card">
        <div class="card-title">Distribuição por Status</div>

        <div class="status-layout">
          <div class="donut-wrap">
            <div class="donut-chart">
              <div class="donut-center">
                <strong>{{ totalStatus }}</strong>
                <span>processos</span>
              </div>
            </div>
          </div>

          <div class="legend-list">
            <div class="legend-item" v-for="item in statusData" :key="item.label">
              <div class="legend-top">
                <span class="legend-label">{{ item.label }}</span>
                <strong>{{ item.valor }}</strong>
              </div>
              <div class="mini-bar">
                <div class="mini-bar-fill" :style="{ width: getPercent(item.valor, totalStatus) + '%' }"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- processos por tipo -->
      <div class="panel-card">
        <div class="card-title">Processos por Tipo</div>

        <div class="bar-list">
          <div class="bar-item" v-for="item in tiposData" :key="item.label">
            <div class="bar-header">
              <span>{{ item.label }}</span>
              <strong>{{ item.valor }}</strong>
            </div>
            <div class="bar-track">
              <div class="bar-fill" :style="{ width: getPercent(item.valor, maxTipo) + '%' }"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- linha 2 -->
    <div class="charts-grid charts-grid-2">
      <!-- evolução mensal -->
      <div class="panel-card">
        <div class="card-title">Evolução Mensal de Protocolos / Análises</div>

        <div class="column-chart">
          <div class="column-item" v-for="item in evolucaoData" :key="item.mes">
            <div class="column-value">{{ item.valor }}</div>
            <div class="column-track">
              <div
                class="column-fill"
                :style="{ height: getPercent(item.valor, maxEvolucao) + '%' }"
              ></div>
            </div>
            <div class="column-label">{{ item.mes }}</div>
          </div>
        </div>
      </div>

      <!-- pendências por estabelecimento -->
      <div class="panel-card">
        <div class="card-title">Pendências por Estabelecimento</div>

        <div class="bar-list">
          <div class="bar-item" v-for="item in pendenciasData" :key="item.nome">
            <div class="bar-header">
              <span>{{ item.nome }}</span>
              <strong>{{ item.valor }}</strong>
            </div>
            <div class="bar-track">
              <div class="bar-fill warning" :style="{ width: getPercent(item.valor, maxPendencias) + '%' }"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- tabela -->
    <div class="panel-card">
      <div class="card-title">Últimos Processos / Documentos</div>

      <div class="table-wrapper">
        <table class="data-table">
          <thead>
            <tr>
              <th style="width: 130px;">Protocolo</th>
              <th>Estabelecimento</th>
              <th style="width: 200px;">Tipo</th>
              <th style="width: 130px;">Envio</th>
              <th style="width: 150px;">Status</th>
              <th style="width: 120px;">Pendências</th>
              <th style="width: 120px;">Prazo</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in ultimosProcessos" :key="item.id">
              <td>{{ item.protocolo }}</td>
              <td>{{ item.estabelecimento }}</td>
              <td>{{ item.tipo }}</td>
              <td>{{ item.dataEnvio }}</td>
              <td>
                <span class="badge" :class="statusClass(item.status)">
                  {{ item.status }}
                </span>
              </td>
              <td>{{ item.pendencias }}</td>
              <td>{{ item.prazo }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- observações -->
    <div class="panel-card">
      <div class="card-title">Origem dos Dados</div>

      <div class="info-grid">
        <div class="info-box">
          <strong>Processos</strong>
          <p>Registro de estabelecimentos, produtos, rótulos, projetos e demais protocolos vinculados ao SIM.</p>
        </div>

        <div class="info-box">
          <strong>Documentos / Projetos</strong>
          <p>Arquivos anexados, versões, datas de envio, situação da análise e exigências registradas.</p>
        </div>

        <div class="info-box">
          <strong>Histórico / Tramitação</strong>
          <p>Movimentações, reanálises, aprovações, pendências, usuários responsáveis e prazos.</p>
        </div>

        <div class="info-box">
          <strong>Indicadores</strong>
          <p>Os gráficos são consolidados a partir dos dados operacionais já existentes nos módulos do sistema.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, computed, onMounted } from 'vue';

const filtros = reactive({
  periodoInicial: '2026-01-01',
  periodoFinal: '2026-03-20',
  municipio: 'Todos',
  tipoProcesso: 'Todos',
  status: 'Todos'
});

const kpis = reactive({
  totalProcessos: 0,
  emAnalise: 0,
  aprovados: 0,
  comPendencias: 0,
  reprovados: 0,
  tempoMedioDias: 0
});

const statusData = ref([]);
const tiposData = ref([]);
const evolucaoData = ref([]);
const pendenciasData = ref([]);
const ultimosProcessos = ref([]);

const carregarMock = () => {
  Object.assign(kpis, {
    totalProcessos: 128,
    emAnalise: 34,
    aprovados: 61,
    comPendencias: 25,
    reprovados: 8,
    tempoMedioDias: 12
  });

  statusData.value = [
    { label: 'Aprovados', valor: 61 },
    { label: 'Em análise', valor: 34 },
    { label: 'Com pendências', valor: 25 },
    { label: 'Reprovados', valor: 8 }
  ];

  tiposData.value = [
    { label: 'Projetos arquitetônicos', valor: 42 },
    { label: 'Registro de rótulos', valor: 31 },
    { label: 'Registro de produtos', valor: 27 },
    { label: 'Registro de estabelecimentos', valor: 18 },
    { label: 'Outros documentos', valor: 10 }
  ];

  evolucaoData.value = [
    { mes: 'Jan', valor: 28 },
    { mes: 'Fev', valor: 35 },
    { mes: 'Mar', valor: 41 },
    { mes: 'Abr', valor: 33 },
    { mes: 'Mai', valor: 29 },
    { mes: 'Jun', valor: 37 }
  ];

  pendenciasData.value = [
    { nome: 'Frigorífico Modelo Ltda', valor: 7 },
    { nome: 'Laticínio Serra Azul', valor: 5 },
    { nome: 'Entreposto Vale do Mel', valor: 4 },
    { nome: 'Pescados Costa Sul', valor: 3 },
    { nome: 'Ovos Colonial do Vale', valor: 2 }
  ];

  ultimosProcessos.value = [
    {
      id: 1,
      protocolo: 'PROC-2026/0142',
      estabelecimento: 'Frigorífico Modelo Ltda',
      tipo: 'Projeto Arquitetônico',
      dataEnvio: '18/03/2026',
      status: 'Com pendências',
      pendencias: 3,
      prazo: '28/03/2026'
    },
    {
      id: 2,
      protocolo: 'PROC-2026/0141',
      estabelecimento: 'Laticínio Serra Azul',
      tipo: 'Registro de Produto',
      dataEnvio: '17/03/2026',
      status: 'Em análise',
      pendencias: 1,
      prazo: '25/03/2026'
    },
    {
      id: 3,
      protocolo: 'PROC-2026/0138',
      estabelecimento: 'Entreposto Vale do Mel',
      tipo: 'Registro de Rótulo',
      dataEnvio: '16/03/2026',
      status: 'Aprovado',
      pendencias: 0,
      prazo: '-'
    },
    {
      id: 4,
      protocolo: 'PROC-2026/0136',
      estabelecimento: 'Pescados Costa Sul',
      tipo: 'Registro de Estabelecimento',
      dataEnvio: '15/03/2026',
      status: 'Reprovado',
      pendencias: 4,
      prazo: '-'
    },
    {
      id: 5,
      protocolo: 'PROC-2026/0135',
      estabelecimento: 'Ovos Colonial do Vale',
      tipo: 'Projeto Arquitetônico',
      dataEnvio: '14/03/2026',
      status: 'Aprovado',
      pendencias: 0,
      prazo: '-'
    }
  ];
};

const totalStatus = computed(() =>
  statusData.value.reduce((acc, item) => acc + item.valor, 0)
);

const maxTipo = computed(() =>
  Math.max(...tiposData.value.map(item => item.valor), 1)
);

const maxEvolucao = computed(() =>
  Math.max(...evolucaoData.value.map(item => item.valor), 1)
);

const maxPendencias = computed(() =>
  Math.max(...pendenciasData.value.map(item => item.valor), 1)
);

const getPercent = (valor, total) => {
  if (!total) return 0;
  return Math.round((valor / total) * 100);
};

const statusClass = (status) => {
  return {
    'badge-info': status === 'Em análise',
    'badge-success': status === 'Aprovado',
    'badge-warning': status === 'Com pendências',
    'badge-danger': status === 'Reprovado'
  };
};

onMounted(() => {
  carregarMock();
});
</script>

<style scoped>
.dashboard-page {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: #f6f8fb;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.page-header h2 {
  margin: 0;
  font-size: 24px;
  color: #1f2937;
}

.page-subtitle {
  margin: 6px 0 0 0;
  color: #6b7280;
  max-width: 900px;
}

.header-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.panel-card,
.kpi-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.panel-card {
  padding: 16px;
}

.card-title {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 14px;
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(160px, 1fr));
  gap: 12px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field label {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}

.field input,
.field select {
  width: 100%;
  min-height: 38px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 14px;
  box-sizing: border-box;
}

.field-actions {
  justify-content: flex-end;
}

.btn {
  border: none;
  border-radius: 8px;
  padding: 10px 14px;
  font-weight: 600;
  cursor: pointer;
  min-height: 38px;
}

.btn-full {
  width: 100%;
}

.btn-primary {
  background: #2563eb;
  color: #fff;
}

.btn-secondary {
  background: #e5e7eb;
  color: #111827;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(140px, 1fr));
  gap: 12px;
}

.kpi-card {
  padding: 16px;
}

.kpi-label {
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 8px;
}

.kpi-value {
  font-size: 28px;
  font-weight: 700;
  color: #111827;
}

.text-info {
  color: #2563eb;
}

.text-success {
  color: #15803d;
}

.text-warning {
  color: #b45309;
}

.text-danger {
  color: #b91c1c;
}

.charts-grid {
  display: grid;
  gap: 16px;
}

.charts-grid-2 {
  grid-template-columns: 1fr 1fr;
}

.status-layout {
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 16px;
  align-items: center;
}

.donut-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
}

.donut-chart {
  width: 180px;
  height: 180px;
  border-radius: 50%;
  background:
    conic-gradient(
      #16a34a 0deg 171deg,
      #2563eb 171deg 267deg,
      #f59e0b 267deg 337deg,
      #dc2626 337deg 360deg
    );
  display: flex;
  align-items: center;
  justify-content: center;
}

.donut-center {
  width: 96px;
  height: 96px;
  border-radius: 50%;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.donut-center strong {
  font-size: 24px;
  color: #111827;
  line-height: 1;
}

.donut-center span {
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
}

.legend-list,
.bar-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.legend-item,
.bar-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.legend-top,
.bar-header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 14px;
  color: #374151;
}

.legend-label {
  font-weight: 600;
}

.mini-bar,
.bar-track {
  height: 10px;
  background: #e5e7eb;
  border-radius: 999px;
  overflow: hidden;
}

.mini-bar-fill,
.bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #2563eb, #60a5fa);
  border-radius: 999px;
}

.bar-fill.warning {
  background: linear-gradient(90deg, #f59e0b, #fbbf24);
}

.column-chart {
  min-height: 260px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 6px 0 6px;
}

.column-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  min-width: 50px;
}

.column-value {
  font-size: 13px;
  font-weight: 700;
  color: #374151;
}

.column-track {
  width: 100%;
  height: 180px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: #f3f4f6;
  border-radius: 10px 10px 0 0;
  padding: 0 8px;
}

.column-fill {
  width: 100%;
  border-radius: 10px 10px 0 0;
  background: linear-gradient(180deg, #60a5fa, #2563eb);
  min-height: 12px;
}

.column-label {
  font-size: 13px;
  color: #4b5563;
  font-weight: 600;
}

.table-wrapper {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th,
.data-table td {
  border-bottom: 1px solid #e5e7eb;
  padding: 10px;
  text-align: left;
  font-size: 14px;
  vertical-align: middle;
}

.data-table th {
  background: #f9fafb;
  color: #374151;
  font-weight: 700;
}

.badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.badge-info {
  background: #dbeafe;
  color: #1d4ed8;
}

.badge-success {
  background: #dcfce7;
  color: #166534;
}

.badge-warning {
  background: #fef3c7;
  color: #92400e;
}

.badge-danger {
  background: #fee2e2;
  color: #991b1b;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(180px, 1fr));
  gap: 12px;
}

.info-box {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 12px;
  background: #fafafa;
}

.info-box strong {
  display: block;
  margin-bottom: 6px;
  color: #111827;
}

.info-box p {
  margin: 0;
  color: #4b5563;
  font-size: 14px;
  line-height: 1.45;
}

@media (max-width: 1200px) {
  .filters-grid {
    grid-template-columns: repeat(3, minmax(160px, 1fr));
  }

  .kpi-grid {
    grid-template-columns: repeat(3, minmax(140px, 1fr));
  }

  .charts-grid-2,
  .info-grid {
    grid-template-columns: 1fr;
  }

  .status-layout {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
  }

  .filters-grid,
  .kpi-grid {
    grid-template-columns: 1fr;
  }

  .column-chart {
    overflow-x: auto;
    justify-content: flex-start;
  }

  .column-item {
    min-width: 70px;
  }
}
</style>